import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Truck, Search, MapPin, Bell, User, LogOut,
  Settings, ChevronDown, X, Package, Menu,
} from 'lucide-react';
import { AuthUser, hasAnyPermission, hasPermission } from '../api/auth';
import { STORAGE_KEYS } from '../constants/storage-keys';
import { isRepOnlyPortalUser, isWarehouseOnlyPortalUser, resolveHomePath } from '../config/navigation';
import { useViewport } from '../hooks/useViewport';

// ── Route → section label mapping (longest prefix wins) ───────────────────
const SECTION_MAP: Record<string, string> = {
  '/':                          'Главная',
  '/all-orders':                'Все заявки',
  '/ttn-deviations':            'Отклонения ТТН',
  '/all-rounds':                'Все рейсы',
  '/orders/new':                'Новая заявка',
  '/unified-orders':            'Заявка',
  '/rounds/new':                'Новый рейс',
  '/rounds':                    'Рейс',
  '/rep/rounds':                'Скан рейса',
  '/rep/stock':                 'Остатки на складе',
  '/rep/pickups':               'Заборы',
  '/rep/xd-outbound':           'Перегруз',
  '/rep/linehaul':              'Скан рейса',
  '/rep/orders/new':            'Новая заявка',
  '/rep/rp-rounds':             'Рейсы водителей',
  '/rep/drivers':               'Водители',
  '/rep/pod':                   'Вручение',
  '/rep/ops':                   'Реестр перевозок',
  '/rep/settlements':           'Мои расчёты',
  '/rep-registry':              'Мой реестр',
  '/admin/rp-rounds':           'Рейсы РП (обзор)',
  '/regional-representatives':  'Исполнители',
  '/receiving':                 'Приёмка грузов',
  '/acceptance-reports':        'Акты приёмки',
  '/warehouse/stock':           'Склад — Остатки',
  '/warehouse/portal':          'Сегодня',
  '/client/orders/new':          'Новый предзаказ',
  '/client/orders':              'Предзаказы',
  '/client/accounts':            'Учётки филиалов',
  '/client/ttn/new':             'Новый предзаказ',
  '/warehouse/rounds':          'Рейсы',
  '/warehouse/loading':         'Погрузка',
  '/warehouse/receive':         'Приёмка',
  '/reports/margin':            'Отчёт по марже',
  '/reports':                   'Отчётность',
  '/finance':                   'Финансы',
  '/users':                     'Пользователи',
  '/roles':                     'Роли и права',
  '/clients':                   'Клиенты',
  '/contact-faces':             'Контактные лица',
  '/branches':                  'Филиалы',
  '/drivers':                   'Водители',
  '/transports':                'Транспорт',
  '/carriers':                  'Перевозчики',
  '/couriers':                  'Курьеры',
  '/tarifs':                    'Тарифы',
  '/additional-services':       'Доп. услуги',
  '/round-extra-services':      'Доп. расходы рейса',
  '/cities':                    'Города',
  '/directions':                'Направления',
  '/delivery-times':            'Сроки доставки',
  '/route-directory':           'Справочник маршрутов',
  '/box-types':                 'Типы коробок',
  '/cost-types':                'Типы расходов',
  '/banks':                     'Банки',
  '/exchange-rates':            'Курсы валют',
  '/transport-types':           'Типы транспорта',
  '/personals':                 'Персонал',
  '/firms':                     'Фирмы',
  '/incidents':                 'Инциденты',
  '/resolutions':               'Резолюции',
  '/messages':                  'Сообщения',
  '/audit-log':                 'Журнал событий',
  '/tester-feedback':           'Баги тестеров',
  '/email-queue':               'Email очередь',
  '/representative/dashboard':  'Сегодня',
}

function getSectionLabel(pathname: string): string {
  if (/^\/client\/orders\/[^/]+\/ttn/.test(pathname)) return 'Прикрепить ТТН';
  if (SECTION_MAP[pathname]) return SECTION_MAP[pathname];
  const prefix = Object.keys(SECTION_MAP)
    .filter(k => k !== '/')
    .sort((a, b) => b.length - a.length)
    .find(k => pathname.startsWith(k));
  return prefix ? SECTION_MAP[prefix] : 'WM IAA';
}

function readStoredUser(): AuthUser | null {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEYS.USER) || 'null');
  } catch {
    return null;
  }
}

interface TopBarProps {
  onLogout?: () => void;
  /** Phone: open mobile nav drawer */
  onMenuClick?: () => void;
}

export function TopBar({ onLogout, onMenuClick }: TopBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isPhone, isCompact, isDesktop } = useViewport();

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);

  const searchRef = useRef<HTMLInputElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  const user = readStoredUser();
  const perms: string[] = user && Array.isArray(user.permissions) ? user.permissions : [];
  const isRepOnly = isRepOnlyPortalUser(perms);
  const isWarehouseOnly = isWarehouseOnlyPortalUser(perms);
  const canSearchOrders = !isRepOnly && !isWarehouseOnly && hasAnyPermission(['orders.view', 'city-orders.view']);
  const canQuickCreateOrder = !isRepOnly && !isWarehouseOnly && hasAnyPermission(['orders.create', 'city-orders.create']);
  const canQuickCreateRound = !isRepOnly && !isWarehouseOnly && hasAnyPermission(['rounds.create', 'city-rounds.create']);
  const canOpenUsers = hasAnyPermission(['users.view', 'users.manage']);
  const canOpenRoles = hasAnyPermission(['roles.view', 'roles.manage']) || hasPermission('users.manage');
  const homePath = resolveHomePath(perms, undefined, user?.roleName);

  const sectionLabel = isRepOnly && !hasPermission('rep.rounds.view') && location.pathname.startsWith('/rep/rp-rounds') ? 'Мой рейс' : getSectionLabel(location.pathname);

  const repName = user?.representativeName?.trim() || '';
  const repCities = (user?.representativeCities ?? [])
    .map(c => c.fullname)
    .filter(Boolean)
    .join(', ');
  // For portal RP: show CRM card name («Алматы РП») as primary label
  const displayName = (isRepOnly && repName)
    ? repName
    : (user?.fullname || user?.username || 'Профиль');
  const roleLabel = isRepOnly
    ? (repName
      ? (repCities ? `РП · ${repCities}` : (user?.roleName || 'РП Портал'))
      : (user?.roleName || 'РП Портал'))
    : isWarehouseOnly
      ? (user?.roleName || 'Склад хаба')
      : (user?.roleName || 'Пользователь');
  const initialsSource = (isRepOnly && repName) ? repName : (user?.fullname || user?.username || '?');
  const initials = initialsSource
    .split(/\s+/)
    .filter(Boolean)
    .map((p: string) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

  // Ctrl+K → open search (only when search is available)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        if (!canSearchOrders) return;
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setSearchValue('');
        setProfileOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [canSearchOrders]);

  // Close profile dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!searchValue.trim() || !canSearchOrders) return;
    navigate(`/all-orders?search=${encodeURIComponent(searchValue.trim())}`);
    setSearchOpen(false);
    setSearchValue('');
  }, [searchValue, navigate, canSearchOrders]);

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    onLogout?.();
    window.location.href = '/login';
  };

  const showQuickActions = canQuickCreateOrder || canQuickCreateRound;
  const showProfileAdminLinks = canOpenUsers || canOpenRoles;

  return (
    <header
      className={`topbar${isPhone ? ' topbar--phone' : ''}${isCompact ? ' topbar--compact' : ''}`}
      data-testid="topbar"
      data-role={roleLabel}
      data-rep-only={isRepOnly ? '1' : '0'}
    >
      {/* ── Left: Logo + Current Section ── */}
      <div className="topbar__left">
        {onMenuClick && (
          <button
            type="button"
            className="topbar__menu-btn"
            onClick={onMenuClick}
            aria-label="Открыть меню"
            data-testid="topbar-menu"
          >
            <Menu size={22} />
          </button>
        )}
        <button
          type="button"
          className="topbar__logo"
          onClick={() => navigate(homePath)}
          title="Главная"
          data-testid="topbar-logo"
        >
          <span className="topbar__logo-icon">
            <Truck size={18} strokeWidth={2.5} />
          </span>
          <span className="topbar__logo-text">WM IAA</span>
        </button>

        {sectionLabel !== 'WM IAA' && sectionLabel !== 'Главная' && (
          <>
            <span className="topbar__section-sep" aria-hidden>/</span>
            <span className="topbar__section" data-testid="topbar-section">{sectionLabel}</span>
          </>
        )}
      </div>

      {/* ── Center: Global Search (TMS roles with orders.view) ── */}
      <div className="topbar__center">
        {canSearchOrders ? (
          searchOpen ? (
            <form className="topbar__search topbar__search--active" onSubmit={handleSearchSubmit} data-testid="topbar-search-form">
              <Search size={15} className="topbar__search-icon" />
              <input
                ref={searchRef}
                className="topbar__search-input"
                placeholder="Поиск по заказам, клиентам, маршрутам..."
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                autoFocus
                aria-label="Глобальный поиск"
              />
              {searchValue && (
                <button type="button" className="topbar__search-clear" onClick={() => setSearchValue('')} aria-label="Очистить">
                  <X size={14} />
                </button>
              )}
              <kbd className="topbar__kbd" onClick={() => { setSearchOpen(false); setSearchValue(''); }}>Esc</kbd>
            </form>
          ) : (
            <button
              type="button"
              className="topbar__search topbar__search--trigger"
              onClick={() => { setSearchOpen(true); setTimeout(() => searchRef.current?.focus(), 50); }}
              data-testid="topbar-search-trigger"
            >
              <Search size={15} className="topbar__search-icon" />
              <span className="topbar__search-placeholder">Поиск...</span>
              <kbd className="topbar__kbd">Ctrl K</kbd>
            </button>
          )
        ) : (
          <div className="topbar__center-spacer" aria-hidden data-testid="topbar-search-hidden" />
        )}
      </div>

      {/* ── Right: Actions + Notifications + Profile ── */}
      <div className="topbar__right">
        {showQuickActions && (
          <>
            <div className="topbar__quick-actions" data-testid="topbar-quick-actions">
              {canQuickCreateOrder && (
                <button
                  type="button"
                  className="topbar__icon-btn"
                  onClick={() => navigate('/orders/new')}
                  title="Создать заказ"
                  data-testid="topbar-create-order"
                >
                  <Package size={18} />
                  <span className="topbar__icon-btn-label">Заказ</span>
                </button>
              )}
              {canQuickCreateRound && (
                <button
                  type="button"
                  className="topbar__icon-btn"
                  onClick={() => navigate('/rounds/new')}
                  title="Создать рейс"
                  data-testid="topbar-create-round"
                >
                  <MapPin size={18} />
                  <span className="topbar__icon-btn-label">Рейс</span>
                </button>
              )}
            </div>
            <div className="topbar__divider" />
          </>
        )}

        {/* Notifications → messages */}
        <button
          type="button"
          className="topbar__icon-btn topbar__notif-btn"
          title="Сообщения"
          onClick={() => navigate('/messages')}
          data-testid="topbar-messages"
        >
          <Bell size={18} />
        </button>

        <div className="topbar__divider" />

        {/* Profile dropdown */}
        <div className="topbar__profile-wrap" ref={profileRef}>
          <button
            type="button"
            className={`topbar__profile-btn${profileOpen ? ' topbar__profile-btn--open' : ''}`}
            onClick={() => setProfileOpen(o => !o)}
            data-testid="topbar-profile"
            aria-expanded={profileOpen}
          >
            <span className="topbar__avatar">{initials}</span>
            <span className="topbar__profile-meta">
              <span className="topbar__profile-name" title={displayName}>{displayName}</span>
              <span className="topbar__profile-role" title={roleLabel} data-testid="topbar-role">{roleLabel}</span>
            </span>
            <ChevronDown size={14} className={`topbar__chevron${profileOpen ? ' topbar__chevron--open' : ''}`} />
          </button>

          {profileOpen && (
            <div className="topbar__dropdown" data-testid="topbar-dropdown">
              <div className="topbar__dropdown-header">
                <span className="topbar__avatar topbar__avatar--lg">{initials}</span>
                <div>
                  <div className="topbar__dropdown-name">{displayName}</div>
                  <div className="topbar__dropdown-role">{roleLabel}</div>
                  {isRepOnly && repName && user?.fullname && user.fullname !== repName && (
                    <div className="topbar__dropdown-sub" data-testid="topbar-rep-user">
                      {user.fullname}
                    </div>
                  )}
                  {isRepOnly && repName && (
                    <div className="topbar__dropdown-sub" data-testid="topbar-rep-org">
                      Исполнитель: {repName}
                      {repCities ? ` · ${repCities}` : ''}
                    </div>
                  )}
                </div>
              </div>
              <div className="topbar__dropdown-sep" />
              {showProfileAdminLinks && (
                <>
                  {canOpenUsers && (
                    <button
                      type="button"
                      className="topbar__dropdown-item"
                      onClick={() => { setProfileOpen(false); navigate('/users'); }}
                      data-testid="topbar-menu-users"
                    >
                      <User size={15} />
                      Пользователи
                    </button>
                  )}
                  {canOpenRoles && (
                    <button
                      type="button"
                      className="topbar__dropdown-item"
                      onClick={() => { setProfileOpen(false); navigate('/roles'); }}
                      data-testid="topbar-menu-roles"
                    >
                      <Settings size={15} />
                      Роли и права
                    </button>
                  )}
                  <div className="topbar__dropdown-sep" />
                </>
              )}
              <button
                type="button"
                className="topbar__dropdown-item"
                onClick={() => { setProfileOpen(false); navigate('/security'); }}
                data-testid="topbar-menu-security"
              >
                <Settings size={15} />
                Безопасность (MFA)
              </button>
              <button
                type="button"
                className="topbar__dropdown-item topbar__dropdown-item--danger"
                onClick={handleLogout}
                data-testid="topbar-logout"
              >
                <LogOut size={15} />
                Выйти
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
